import { FC, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Paper } from "@mui/material";
import {
  GridFilterModel,
  GridLogicOperator,
  GridPaginationModel,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import { isNumber } from "lodash";
import __ from "src/utils/Translations";
import useBasePath from "src/utils/hooks/useBasePath";
import { useFetchManagedAbsenceRequests } from "src/Pages/Company/Absences/api/absenceRequests.queries";
import { useFetchEmployee } from "src/Pages/Company/Absences/api/employee.queries";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import parseTableFilters from "src/utils/Company/Absences/parseTableFilters";
import BreadcrumbsMui from "src/Components/BreadcrumbsMui/BreadcrumbsMui";
import AbsenceRequestsTable from "src/Pages/Company/Absences/AbsenceRequests/AbsenceRequestsTable";
import EmployeeCard from "src/Pages/Company/Absences/EmployeeCard";
import EmployeeCardSkeleton from "src/Pages/Company/Absences/EmployeeCard/EmployeeCardSkeleton";
import Title from "src/Pages/Company/Absences/Title/Title";

const AbsenceRecordsTypeRequests: FC = () => {
  const { employeeId, absenceTypeId } = useParams<{
    employeeId: string;
    absenceTypeId: string;
  }>();
  const companyId = useCompanyId();
  const basePath = useBasePath();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
    logicOperator: GridLogicOperator.And,
  });

  const requestParams = useMemo(
    () => ({
      page: paginationModel.page + 1,
      size: paginationModel.pageSize,
      sort: sortModel?.map(({ field, sort }) => `${field},${sort}`),
      filters: parseTableFilters(filterModel, [
        ...(employeeId ? [{ key: "employeeIds", value: [employeeId] }] : []),
        ...(absenceTypeId
          ? [{ key: "absenceTypeIds", value: [absenceTypeId] }]
          : []),
      ]),
    }),
    [
      absenceTypeId,
      employeeId,
      filterModel,
      paginationModel.page,
      paginationModel.pageSize,
      sortModel,
    ]
  );

  const { data: employee, isFetching: isFetchingEmployee } =
    useFetchEmployee(employeeId);

  const { data, isFetching } = useFetchManagedAbsenceRequests({
    companyId,
    ...requestParams,
  });

  const rowCountRef = useRef(data?.totalElements || 0);
  const rowCount = useMemo(() => {
    if (isNumber(data?.totalElements)) {
      rowCountRef.current = data.totalElements;
    }
    return rowCountRef.current;
  }, [data?.totalElements]);

  return (
    <>
      <BreadcrumbsMui
        items={[
          {
            label: __("Nieobecności"),
            to: `${basePath}/absences`,
          },
          {
            label: __("Kartoteki pracowników"),
            to: `${basePath}/absences/absence-records`,
          },
          {
            label: __("Wymiary nieobecności i obecności"),
            to: `${basePath}/absences/employee/${employeeId}#balances`,
          },
          {
            label: __("Lista złożonych wniosków"),
          },
        ]}
      />
      <Title title={__("Lista złożonych wniosków")} />
      {isFetchingEmployee ? (
        <EmployeeCardSkeleton />
      ) : (
        <EmployeeCard data={employee} />
      )}
      <Paper data-testid="absence-records-type-requests">
        <AbsenceRequestsTable
          loading={isFetching}
          rows={data?.content || []}
          rowCount={rowCount}
          storageKey="absence-request-type-table-state"
          sortModel={sortModel}
          setSortModel={setSortModel}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
          filterModel={filterModel}
          setFilterModel={setFilterModel}
          showEmployeeData={false}
        />
      </Paper>
    </>
  );
};

export default AbsenceRecordsTypeRequests;
